/**
 * API Service Layer
 *
 * Split deployment: set VITE_PUBLIC_API_URL, VITE_CUSTOMER_API_URL, VITE_ADMIN_API_URL.
 * Monolith / local: set only VITE_API_URL (all calls use the same base).
 */

const DEFAULT_BASE = 'http://localhost:3000/api/v1';

export function getPublicApiBase(): string {
  return import.meta.env.VITE_PUBLIC_API_URL || import.meta.env.VITE_API_URL || DEFAULT_BASE;
}

export function getCustomerApiBase(): string {
  return import.meta.env.VITE_CUSTOMER_API_URL || import.meta.env.VITE_API_URL || DEFAULT_BASE;
}

export function getAdminApiBase(): string {
  return import.meta.env.VITE_ADMIN_API_URL || import.meta.env.VITE_API_URL || DEFAULT_BASE;
}

function getAuthApiBase(): string {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
    return getAdminApiBase();
  }
  return getCustomerApiBase();
}

// Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    user: {
      user_id: string;
      email: string;
      first_name: string;
      last_name: string;
      role: string;
    };
    access_token: string;
    refresh_token: string;
  };
}

/** Signed-in customer as returned by `/auth/me`. `email` (sign-in ID) and `phone` are read-only. */
export interface CustomerProfile {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  role: string;
}

/** Editable profile fields (`PATCH /auth/me`); email (sign-in ID) and phone are deliberately absent. */
export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
}

/** Storefront order as returned by `/orders` (amounts computed on the server). */
export interface StorefrontOrder {
  order_number: string;
  placed_at: string;
  order_status: string;
  payment_status: string;
  payment_method: string | null;
  customer: { first_name: string; last_name: string; email: string; phone: string | null };
  notes: string;
  lines: {
    inventory_id: string | null;
    name: string;
    item_num: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }[];
  subtotal: number;
  tax: number;
  tax_rate_label: string;
  total: number;
}

/** Row in the customer's order history (`GET /orders`). */
export interface StorefrontOrderSummary {
  order_number: string;
  placed_at: string;
  order_status: string;
  payment_status: string;
  item_count: number;
  line_count: number;
  first_items: string[];
  total: number;
}

export interface PlaceOrderRequest {
  lines: { inventory_id: string; quantity: number }[];
  notes?: string;
}

export interface Product {
  product_id: string;
  product_name: string;
  slug: string;
  description?: string;
  size?: string;
  unit?: string;
  price: number;
  mrp?: number;
  discount_percentage?: number;
  sku: string;
  stock_quantity: number;
  is_featured: boolean;
  is_active: boolean;
  department_id?: string;
  brand_id?: string;
  department?: Department;
  brand?: Brand;
  product_images?: ProductImage[];
}

export interface DepartmentCategoryRef {
  category_id: string;
  name: string;
  slug: string;
}

export interface Department {
  department_id: string;
  department_name: string;
  slug: string;
  description?: string;
  image_url?: string;
  display_order?: number;
  is_active: boolean;
  /** Populated on public `/public/departments` responses */
  category?: DepartmentCategoryRef | null;
}

/** High-level category (from `categories` table) — public site only */
export interface StorefrontCategory {
  category_id: string;
  name: string;
  slug: string;
  description?: string | null;
  display_order?: number | null;
}

export interface InventoryItemNameRow {
  inventory_id: string;
  item_num: string;
  item_name: string;
  category_id?: string | null;
  /** CSV/list price (e.g. before discount); optional reference. */
  list_price?: number | null;
  retail_price?: number;
  /** 1–50 when on clearance; `discounted_price` is derived on the server from `retail_price`. */
  discount_percentage?: number | null;
  discounted_price?: number | null;
  in_stock?: number;
  description?: string | null;
  size_label?: string | null;
  brand_name?: string | null;
}

export interface InventoryItemNamesPayload {
  total: number;
  items: InventoryItemNameRow[];
}

export type FeedbackDeviceCategory = 'mobile' | 'tablet' | 'desktop' | 'unknown';

export interface CustomerFeedbackLocation {
  latitude: number;
  longitude: number;
  accuracy_m?: number;
}

export interface CustomerFeedbackClientContext {
  user_agent: string;
  device_category: FeedbackDeviceCategory;
  location?: CustomerFeedbackLocation;
}

export interface CustomerFeedbackSubmit {
  rating: number;
  topic: 'store' | 'web';
  description: string;
  email?: string;
  phone?: string;
  client_context: CustomerFeedbackClientContext;
}

export interface ItemRequestSubmit {
  requester_name: string;
  brand_name: string;
  item_name: string;
  description: string;
  email?: string;
  phone?: string;
  client_context: CustomerFeedbackClientContext;
}

export interface Brand {
  brand_id: string;
  brand_name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  is_active: boolean;
}

export interface ProductImage {
  image_id: string;
  image_url: string;
  alt_text?: string;
  is_primary: boolean;
}

export interface ProductInput {
  product_name: string;
  description?: string;
  size?: string;
  unit?: string;
  price: number;
  mrp?: number;
  sku: string;
  stock_quantity: number;
  is_featured?: boolean;
  is_active?: boolean;
  department_id?: string;
  brand_id?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface SalesAnalytics {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  revenueGrowth: number;
  monthlySales: { month: string; sales: number; orders: number; profit: number }[];
  salesByDepartment: { name: string; value: number; color: string }[];
  salesByBrand: { name: string; sales: number; percentage: number }[];
  topProducts: { name: string; sales: number; units: number; trend: number }[];
}

// Helper functions
function getToken(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('srisai_access_token');
  }
  return null;
}

function getHeaders(includeAuth = false): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    let msg =
      (typeof error.message === 'string' && error.message) ||
      (typeof error.detail === 'string' && error.detail) ||
      '';
    if (!msg && Array.isArray(error.detail) && error.detail.length > 0) {
      const first = error.detail[0] as { msg?: string };
      if (typeof first?.msg === 'string') msg = first.msg;
    }
    if (!msg) msg = `HTTP error! status: ${response.status}`;
    throw new Error(msg);
  }
  return response.json();
}

// API Methods
export const api = {
  // ==================== Auth ====================
  auth: {
    login: async (credentials: LoginRequest): Promise<LoginResponse> => {
      const response = await fetch(`${getAuthApiBase()}/auth/login`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(credentials),
      });
      return handleResponse<LoginResponse>(response);
    },

    logout: async (): Promise<void> => {
      await fetch(`${getAuthApiBase()}/auth/logout`, {
        method: 'POST',
        headers: getHeaders(true),
      });
      localStorage.removeItem('srisai_access_token');
      localStorage.removeItem('srisai_admin_user');
    },

    me: async (): Promise<ApiResponse<CustomerProfile>> => {
      const response = await fetch(`${getAuthApiBase()}/auth/me`, {
        headers: getHeaders(true),
      });
      return handleResponse(response);
    },

    updateMe: async (body: UpdateProfileRequest): Promise<ApiResponse<CustomerProfile>> => {
      const response = await fetch(`${getAuthApiBase()}/auth/me`, {
        method: 'PATCH',
        headers: getHeaders(true),
        body: JSON.stringify(body),
      });
      return handleResponse(response);
    },
  },

  // ==================== Storefront orders (signed-in customers) ====================
  orders: {
    list: async (): Promise<ApiResponse<StorefrontOrderSummary[]>> => {
      const response = await fetch(`${getCustomerApiBase()}/orders`, {
        headers: getHeaders(true),
      });
      return handleResponse(response);
    },

    place: async (body: PlaceOrderRequest): Promise<ApiResponse<StorefrontOrder>> => {
      const response = await fetch(`${getCustomerApiBase()}/orders`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify(body),
      });
      return handleResponse(response);
    },

    get: async (orderNumber: string): Promise<ApiResponse<StorefrontOrder>> => {
      const response = await fetch(`${getCustomerApiBase()}/orders/${encodeURIComponent(orderNumber)}`, {
        headers: getHeaders(true),
      });
      return handleResponse(response);
    },
  },

  // ==================== Public storefront (read-only, CDN + server cache ~300s) ====================
  storefront: {
    getCategories: async (): Promise<ApiResponse<StorefrontCategory[]>> => {
      const response = await fetch(`${getPublicApiBase()}/public/categories`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getDepartments: async (): Promise<ApiResponse<Department[]>> => {
      const response = await fetch(`${getPublicApiBase()}/public/departments`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getInventoryItemNames: async (): Promise<ApiResponse<InventoryItemNamesPayload>> => {
      const response = await fetch(`${getPublicApiBase()}/public/inventory-item-names`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    /** Unauthenticated PUT multipart — Origin header sent by browser; optional `attachment` max 500 KB. */
    submitFeedback: async (
      body: CustomerFeedbackSubmit,
      attachment?: File | null
    ): Promise<ApiResponse<{ feedback_id: string }>> => {
      const fd = new FormData();
      fd.append('rating', String(body.rating));
      fd.append('topic', body.topic);
      fd.append('description', body.description);
      fd.append('client_context', JSON.stringify(body.client_context));
      if (body.email) fd.append('email', body.email);
      if (body.phone) fd.append('phone', body.phone);
      if (attachment && attachment.size > 0) fd.append('attachment', attachment);
      const response = await fetch(`${getPublicApiBase()}/public/feedback`, {
        method: 'PUT',
        body: fd,
      });
      return handleResponse(response);
    },

    submitItemRequest: async (
      body: ItemRequestSubmit,
      attachment?: File | null
    ): Promise<ApiResponse<{ item_request_id: string }>> => {
      const fd = new FormData();
      fd.append('requester_name', body.requester_name);
      fd.append('brand_name', body.brand_name);
      fd.append('item_name', body.item_name);
      fd.append('description', body.description);
      fd.append('client_context', JSON.stringify(body.client_context));
      if (body.email) fd.append('email', body.email);
      if (body.phone) fd.append('phone', body.phone);
      if (attachment && attachment.size > 0) fd.append('attachment', attachment);
      const response = await fetch(`${getPublicApiBase()}/public/item-request`, {
        method: 'PUT',
        body: fd,
      });
      return handleResponse(response);
    },
  },

  // ==================== Products ====================
  products: {
    getAll: async (params?: {
      page?: number;
      perPage?: number;
      departmentId?: string;
      brandId?: string;
      search?: string;
      minPrice?: number;
      maxPrice?: number;
      isFeatured?: boolean;
    }): Promise<PaginatedResponse<Product>> => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
      }
      
      const response = await fetch(`${getPublicApiBase()}/products?${searchParams}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getById: async (id: string): Promise<ApiResponse<Product>> => {
      const response = await fetch(`${getPublicApiBase()}/products/${id}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getFeatured: async (): Promise<ApiResponse<Product[]>> => {
      const response = await fetch(`${getPublicApiBase()}/products/featured`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    search: async (query: string): Promise<PaginatedResponse<Product>> => {
      const response = await fetch(`${getPublicApiBase()}/products/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  // ==================== Admin Products ====================
  admin: {
    products: {
      create: async (data: ProductInput): Promise<ApiResponse<Product>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/products`, {
          method: 'POST',
          headers: getHeaders(true),
          body: JSON.stringify(data),
        });
        return handleResponse(response);
      },

      update: async (id: string, data: Partial<ProductInput>): Promise<ApiResponse<Product>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/products/${id}`, {
          method: 'PATCH',
          headers: getHeaders(true),
          body: JSON.stringify(data),
        });
        return handleResponse(response);
      },

      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/products/${id}`, {
          method: 'DELETE',
          headers: getHeaders(true),
        });
        return handleResponse(response);
      },
    },

    departments: {
      getAll: async (): Promise<ApiResponse<Department[]>> => {
        const response = await fetch(`${getPublicApiBase()}/departments`, {
          headers: getHeaders(),
        });
        return handleResponse(response);
      },

      create: async (data: Omit<Department, 'department_id'>): Promise<ApiResponse<Department>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/departments`, {
          method: 'POST',
          headers: getHeaders(true),
          body: JSON.stringify(data),
        });
        return handleResponse(response);
      },

      update: async (id: string, data: Partial<Department>): Promise<ApiResponse<Department>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/departments/${id}`, {
          method: 'PATCH',
          headers: getHeaders(true),
          body: JSON.stringify(data),
        });
        return handleResponse(response);
      },

      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/departments/${id}`, {
          method: 'DELETE',
          headers: getHeaders(true),
        });
        return handleResponse(response);
      },
    },

    brands: {
      getAll: async (): Promise<ApiResponse<Brand[]>> => {
        const response = await fetch(`${getPublicApiBase()}/brands`, {
          headers: getHeaders(),
        });
        return handleResponse(response);
      },

      create: async (data: Omit<Brand, 'brand_id'>): Promise<ApiResponse<Brand>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/brands`, {
          method: 'POST',
          headers: getHeaders(true),
          body: JSON.stringify(data),
        });
        return handleResponse(response);
      },

      update: async (id: string, data: Partial<Brand>): Promise<ApiResponse<Brand>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/brands/${id}`, {
          method: 'PATCH',
          headers: getHeaders(true),
          body: JSON.stringify(data),
        });
        return handleResponse(response);
      },

      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/brands/${id}`, {
          method: 'DELETE',
          headers: getHeaders(true),
        });
        return handleResponse(response);
      },
    },

    analytics: {
      getSalesOverview: async (timeRange: string = 'year'): Promise<ApiResponse<SalesAnalytics>> => {
        const response = await fetch(`${getAdminApiBase()}/admin/analytics/sales?timeRange=${timeRange}`, {
          headers: getHeaders(true),
        });
        return handleResponse(response);
      },

      getProductPerformance: async (productId?: string): Promise<ApiResponse<any>> => {
        const base = getAdminApiBase();
        const url = productId 
          ? `${base}/admin/analytics/products/${productId}`
          : `${base}/admin/analytics/products`;
        const response = await fetch(url, {
          headers: getHeaders(true),
        });
        return handleResponse(response);
      },

      getBrandPerformance: async (brandId?: string): Promise<ApiResponse<any>> => {
        const base = getAdminApiBase();
        const url = brandId
          ? `${base}/admin/analytics/brands/${brandId}`
          : `${base}/admin/analytics/brands`;
        const response = await fetch(url, {
          headers: getHeaders(true),
        });
        return handleResponse(response);
      },
    },
  },
};

export default api;

